<template>
  <section
    v-editable="blok"
    class="grid w-full auto-cols-fr gap-x-2 md:grid-flow-col md:gap-8"
    :class="[
      marginClasses,
      {
        'mx-break-out':
          (!cmsParentBlok || cmsParentBlok.component === 'BlogOverview') &&
          blok.wholeWidth,
      },
      { 'gap-0': blok.removeMargin_sides },
      isMobile ? mobileGridClasses : '',
      { '!mt-10': overrideMarginTop },
    ]"
  >
    <!-- Image is not allowed as a component so we have to rename it here -->
    <!-- override-margin-top is used for InteractableImage component in the mobile layout when the `blok.mobile_layout` is equal to `2x2n` (n means no spacing) -->
    <component
      :is="['Image'].includes(column.component) ? 'CmsImage' : column.component"
      v-for="(column, index) in blok.columns"
      :key="column._uid"
      :blok="column"
      :override-margin-top="overrideMarginTop"
      :affect-cta-button-positioning="isTwoRowLayout"
      :sibling-index="index"
      class="flex min-w-0 max-w-full"
      :class="{
        '!ml-0': isTwoRowLayout,
      }"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue'
import type { PropType } from 'vue'
import type {
  SbGrid,
  BaseStoryblokComponent,
} from '~/cms/storyblok/types/storyblok.d.ts'
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import { useDeviceType } from '~/composables/useDeviceType'
import { GRID_SIBLING_COUNT_KEY } from '../../constants/injectionKeys'

const props = defineProps({
  blok: {
    type: Object as PropType<SbGrid>,
    required: true,
  },
})

provide(GRID_SIBLING_COUNT_KEY, props.blok.columns?.length || 1)

const instance = getCurrentInstance()
const { marginClasses } = useStoryblokMargins(props.blok)
const { isMobile } = useDeviceType()

const cmsParentBlok = computed(
  () =>
    ((instance as any)?.parent?.props?.blok as
      | BaseStoryblokComponent
      | undefined) || null,
)

const mobileGridClasses = computed(() => {
  if (!isMobile.value) return ''

  const layoutMap: Record<string, string> = {
    '1x4': 'grid-cols-1',
    '2x2': 'grid-cols-2 grid-rows-2 space-x-2',
    '2x2n': 'grid-cols-2 grid-rows-2 !gap-0 !space-y-0',
  }

  const layoutKey = props.blok.mobile_layout || '1x4'
  return layoutMap[layoutKey] || 'grid-cols-1'
})

const overrideMarginTop = computed(
  () => isMobile.value && props.blok?.mobile_layout === '2x2n',
)

const isTwoRowLayout = computed(
  () =>
    isMobile.value &&
    (props.blok?.mobile_layout === '2x2n' ||
      props.blok?.mobile_layout === '2x2'),
)
</script>
